// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints } from '../DataPoints';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Components
import { RiskRewardLegendCell } from '@/views/components/cells';
import { ScatterPlotOptions } from '@/views/components/charts/scatter-plot/ScatterPlot';

// Types
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ScatterPlotData from '@/types/scatter-plot-data';
import RiskRewardLegendValue from '@/types/risk-reward-legend-value';
import RiskPeriods from '@/types/risk-periods';
import ComparisonItem from '@/types/comparison-item';

// Utils
import { RISK_METRICS_KEYS, COMPARISON_ITEM_TYPES, TIME_PERIODS } from '@/utils/constants';
import { getRiskRewardChartOptions } from '@/utils/charts';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { monthsToPeriod } from '@/utils/time';

export default class RiskRewardDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new RiskRewardDAO(proposal, proposalElement);
  }

  toolbarOption: ProposalToolbarOption | undefined;

  data: ScatterPlotData[] = [];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.toolbarOption = {};
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build() {
    this.toolbarOption = this.getToolbarOption();
    let period: RiskPeriods = '3Y';

    if (this.toolbarOption && this.toolbarOption.monthsRange) {
      period = monthsToPeriod(this.toolbarOption.monthsRange, this.proposal.monthEndDataOption) as RiskPeriods;
    }

    let baseDAO: ComparisonItemDAO | null = null;
    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (baseDAO === null) {
        baseDAO = itemDAO;
      }
      const value: RiskRewardLegendValue = this.addData(itemDAO.item, period);
      const name: string = this.formatLocalizedString(itemDAO.item.name);
      this.addItemField(baseDAO, itemDAO.item, name, value);
    }
  }

  addData(item: ComparisonItem, period: RiskPeriods): RiskRewardLegendValue {
    const key = `${item._id}`;
    const baseStDevResponse = DataPoints.getRiskMetricsByPeriod(item, RISK_METRICS_KEYS.STANDARD_DEVIATION, period, this.proposal.showBaseCurrencies === true);
    const baseStDev = baseStDevResponse.response;
    const basePeriodReturn = DataPoints.getPercentGrowthByPeriod(item, period, this.proposal.showBaseCurrencies === true);
    const symbol = item.type === COMPARISON_ITEM_TYPES.BENCHMARK ? 'diamond' : 'circle';
    const showSinceInceptionDisclaimer = period === TIME_PERIODS.INC && this.proposal.shortHistory;
    const showMaxCommonDisclaimer = period === TIME_PERIODS.CI && !this.proposal.shortHistory;
    const showCrosshair = item.type === COMPARISON_ITEM_TYPES.BENCHMARK;

    this.addDisclaimerKeys([...baseStDevResponse.disclaimerKeys, ...basePeriodReturn.disclaimerKeys], item);

    if (showSinceInceptionDisclaimer) {
      this.addDisclaimerConfigs([{ key: PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE, items: [item], config: { doNotMergeOnPage: true } }]);
    }

    if (showMaxCommonDisclaimer) {
      this.addDisclaimerConfigs([{ key: PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE, items: [item], config: { doNotMergeOnPage: true } }]);
    }

    if (baseStDev != null && baseStDev.value != null && basePeriodReturn.response != null && basePeriodReturn.response.value != null) {
      this.data.push({
        key,
        data: [
          {
            key,
            x: baseStDev.value,
            y: basePeriodReturn.response.value,
            name: this.formatLocalizedString(item.name),
            color: item.color,
            marker: {
              symbol,
            },
          },
        ],
        showCrosshair,
      });

      const value: RiskRewardLegendValue = {
        name: this.formatLocalizedString(item.name),
        stdDev: baseStDev.value,
        return: basePeriodReturn.response.value,
      };

      return value;
    }

    // Return at least the name so the legend shows the name even if there's no value
    return {
      name: this.formatLocalizedString(item.name),
    };
  }

  // TODO: Centralize formatting of items in RiskRewardPlot.tsx so we can use it here
  getScatterSeries(): ScatterPlotData[] {
    return this.data;
  }

  getScatterChartOptions = (): ScatterPlotOptions => getRiskRewardChartOptions();

  // private methods
  private addItemField(itemDAO: ComparisonItemDAO, item: ComparisonItem, name: string, value: RiskRewardLegendValue): RiskRewardLegendValue | undefined {
    const field: ProposalFieldDAO = new ProposalFieldDAO('', value, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = RiskRewardLegendCell;
    field.color = item.color;
    field.itemType = item.type;
    field.flags = item.flags;
    this.addFieldDAO(itemDAO, field);

    return value;
  }
}
